/**
 * LobbySettings.js
 *
 * Host-only popup in the Auction lobby to set up the game:
 * - Teams: min teams to start, max teams in the room
 * - Constraints: budget, items to buy (min and max), items each team submits
 * - Bidding: the starting bid and the bid step (prices must be one of start, start + step, ...)
 * - Per category attribute of the library (e.g. Actor): different values needed, items per value, and bonuses
 * - Flow: pause after every X items sold, and whether skipped items come back
 * - Visibility: budgets, sales and inventories for everyone
 * Every setting has an ⓘ tip with an example, using the library's own values where it can.
 */

import React, { useState } from "react";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import toast from "react-hot-toast";
import Button from "../../../components/ui/Button";
import { saveAuctionSettings } from "../helpers/auctionLobby";
import { MAX_TEAMS, sortTeams } from "../assets/teamColors";
import { getCategoryAttributes, getRoomLibrary } from "../library/libraries";
import InfoTip from "../../../components/ui/InfoTip";

// Tips: what the setting does, then an example (d = the draft settings)
const teamFields = [
    { key: "minTeams",          label: "Min teams to start",    min: 1,
      tip: (d) => ["The Start button stays locked until this many teams have at least one player.",
                   `e.g. ${d.minTeams || 3}: with players in only ${Math.max(1, (d.minTeams || 3) - 1)} team${(d.minTeams || 3) - 1 === 1 ? "" : "s"}, you can't start yet.`] },
    { key: "maxTeams",          label: `Max teams (up to ${MAX_TEAMS})`, min: 2, max: MAX_TEAMS,
      tip: () => ["How many teams there are in the room for players to join.",
                  "e.g. 4: Team Red, Yellow, Blue and Green. You can't go below a team that already has players."] },
];

const constraintFields = [
    { key: "budgetPerTeam",     label: "Budget per team (₹)",   min: 1, wide: true,
      tip: (d) => ["Each team's money for the whole auction. It goes down with every item it buys.",
                   `e.g. ₹${d.budgetPerTeam || 1000}: after buying one item for ₹${Math.round((d.budgetPerTeam || 1000) * 0.45)}, a team has ₹${(d.budgetPerTeam || 1000) - Math.round((d.budgetPerTeam || 1000) * 0.45)} left.`] },
    { key: "minItemsPerTeam",   label: "Min items to buy",      min: 1,
      tip: (d) => ["A team that buys fewer breaks the rules, and ranks below every team that didn't, whatever its score.",
                   `e.g. ${d.minItemsPerTeam || 6}: a team that ends with ${Math.max(0, (d.minItemsPerTeam || 6) - 1)} items ranks last.`] },
    { key: "maxItemsPerTeam",   label: "Max items to buy",      min: 1,
      tip: (d) => ["Once a team has this many, it can't buy any more: the sale is refused.",
                   `e.g. ${d.maxItemsPerTeam || 7}: selling a ${(d.maxItemsPerTeam || 7) + 1}th item to them is blocked. Set it above "Items to submit" to let teams buy spares.`] },
    { key: "itemsToSubmit",     label: "Items to submit",       min: 1,
      tip: (d) => ["Only the submitted items count: their score, bonuses and rules. Teams that bought more choose which to submit at the end (their best set is suggested).",
                   `e.g. buy up to ${d.maxItemsPerTeam || 7}, submit ${d.itemsToSubmit || 6}: a team with ${d.maxItemsPerTeam || 7} items drops ${Math.max(0, (d.maxItemsPerTeam || 7) - (d.itemsToSubmit || 6))} of them.`] },
];

const bidFields = [
    { key: "startingBid",       label: "Bids start at (₹)",     min: 1,
      tip: (d) => ["No item can sell for less than this.",
                   `e.g. ₹${d.startingBid || 20}: selling for ₹${Math.max(1, Math.floor((d.startingBid || 20) / 2))} is refused.`] },
    { key: "bidStep",           label: "Bid step (₹)",          min: 1,
      tip: (d) => {
          const start = d.startingBid || 20, step = d.bidStep || 10;
          return ["Prices go up from the starting bid in steps of this much. Any other price is refused.",
                  `e.g. start ₹${start}, step ₹${step}: ₹${start}, ₹${start + step}, ₹${start + 2 * step}… are fine${step > 1 ? `; ₹${start + Math.ceil(step / 2)} isn't` : ""}.`];
      } },
];

const ruleFields = [
    { key: "minDifferent",      label: "Min different values",  placeholder: "0" },
    { key: "maxPerValue",       label: "Max per value",         placeholder: "No limit" },
    { key: "bonusOneValue",     label: "Bonus: all one value",  placeholder: "0" },
    { key: "bonusEveryValue",   label: "Bonus: all different",  placeholder: "0" },
];

/**
 * Tip for one rule of a category attribute, with an example from its own values
 * @param {String} key          -   Rule field
 * @param {Object} attribute    -   { name, values }
 * @param {Object} d            -   Draft settings
 * @returns {Array}             -   [what it does, example]
 */
const ruleTip = (key, attribute, d) => {
    const { name, values } = attribute;
    const n = Number(d.itemsToSubmit) || 6;
    const v = (i) => values[i % values.length] ?? `${name} ${i + 1}`;
    const rule = d.attributeRules[name] || {};
    switch (key) {
        case "minDifferent": {
            const min = Math.min(Number(rule.minDifferent) || 3, n, values.length);
            const good = Array.from({ length: n }, (_, i) => v(Math.min(i, min - 1)));
            return [`At least this many different ${name} values among the ${n} submitted items. Repeats are fine.`,
                    `e.g. ${min}: ${good.join(", ")} has ${min} different ✓. All ${n} from ${v(0)} has 1 ✗.`];
        }
        case "maxPerValue": {
            const max = Number(rule.maxPerValue) || 2;
            return [`A team can't buy more than this many items with the same ${name}. Leave blank for no limit.`,
                    `e.g. ${max}: once a team has ${max} from ${v(0)}, selling them another ${v(0)} item is refused.`];
        }
        case "bonusOneValue":
            return [`Extra points if every submitted item has the same ${name}.`,
                    `e.g. +15: all ${n} submitted are ${v(0)}.`];
        default:
            return values.length <= n
                ? [`Extra points if the submission has every ${name} (there are ${values.length}).`,
                   `e.g. +10: the ${n} submitted include ${values.join(", ")}.`]
                : [`Extra points if all ${n} submitted items have a different ${name}. (There are ${values.length}, so a submission can't have every one.)`,
                   `e.g. +10: ${Array.from({ length: n }, (_, i) => v(i)).join(", ")}, no repeats.`];
    }
};

const TipText = ({ lines }) => (
    <>
        <span className="block">{lines[0]}</span>
        <span className="block mt-2 text-zinc-400">{lines[1]}</span>
    </>
);

/**
 * When the 'all different' bonus pays, in words
 * @param {Number} itemsToSubmit    -   Items each team submits
 * @param {Number} valueCount       -   Different values in the library
 * @returns {String}
 */
const allDifferentHint = (itemsToSubmit, valueCount) =>
    valueCount <= itemsToSubmit
        ? `All ${valueCount} values in the submission`
        : `All ${itemsToSubmit} submitted items different`;

const visibilityFields = [
    { key: "showBudgets",       label: "Show every team's budget",      hint: "Teams always see their own." },
    { key: "showSales",         label: "Show who bought each item",     hint: "Buyer and price, on the SOLD screen and phones." },
    { key: "showInventories",   label: "Show every team's items",       hint: "On phones and on the break screen." },
];

const toNumberOrBlank = (value) => (value === "" ? "" : Number(value));
const isWhole = (value) => Number.isInteger(value) && value >= 0;

/**
 * Checks the settings make sense together
 * @param {Object} s                    -   Draft settings
 * @param {Array} teams                 -   Teams in the room, sorted
 * @param {Array} categoryAttributes    -   The library's category attributes, with their values (see getCategoryAttributes)
 * @returns {String|null}               -   The problem, or null if fine
 */
export const getSettingsProblem = (s, teams, categoryAttributes = []) => {
    const numberFields = [...teamFields, ...constraintFields, ...bidFields];
    const numbers = numberFields.map((f) => s[f.key]).concat(s.breakAfter);
    if (!numbers.every(isWhole)) return "All numbers need to be whole numbers.";
    if (numberFields.some(({ key, min, max }) => s[key] < min || (max && s[key] > max))) return "Some numbers are out of range.";
    if (s.minTeams > s.maxTeams) return "Min teams can't be more than max teams.";
    if (s.minItemsPerTeam > s.maxItemsPerTeam) return "Min items to buy can't be more than the max.";
    if (s.itemsToSubmit > s.minItemsPerTeam) return "Items to submit can't be more than the min items to buy.";
    if (s.startingBid * s.minItemsPerTeam > s.budgetPerTeam)
        return `With bids starting at ₹${s.startingBid}, ₹${s.budgetPerTeam} can't buy the min of ${s.minItemsPerTeam} items.`;

    const removedTeamWithPlayers = teams.slice(s.maxTeams).find((team) => team.members?.length > 0);
    if (removedTeamWithPlayers) return `${removedTeamWithPlayers.name} has players, so you can't go below ${teams.indexOf(removedTeamWithPlayers) + 1} teams.`;

    for (const [attribute, rule] of Object.entries(s.attributeRules)) {
        const { minDifferent, maxPerValue, bonusOneValue, bonusEveryValue } = rule;
        if (![minDifferent, bonusOneValue, bonusEveryValue].every(isWhole)) return `${attribute}: use whole numbers.`;
        if (maxPerValue !== null && (!isWhole(maxPerValue) || maxPerValue < 1)) return `${attribute}: max per value must be at least 1, or blank for no limit.`;

        // Rules no team could ever meet
        const valueCount = categoryAttributes.find((a) => a.name === attribute)?.values.length;
        if (minDifferent > s.itemsToSubmit) return `${attribute}: ${minDifferent} different values can't fit in ${s.itemsToSubmit} submitted items.`;
        if (valueCount !== undefined && minDifferent > valueCount) return `${attribute}: there are only ${valueCount} different values.`;
        if (maxPerValue !== null && valueCount !== undefined && maxPerValue * valueCount < s.minItemsPerTeam)
            return `${attribute}: with at most ${maxPerValue} per value, no team can buy ${s.minItemsPerTeam} items.`;
        if (bonusOneValue > 0 && maxPerValue !== null && maxPerValue < s.itemsToSubmit)
            return `${attribute}: the 'all one value' bonus needs ${s.itemsToSubmit} items with one value, but the max per value is ${maxPerValue}.`;
    }
    return null;
};

const Toggle = ({ checked, onChange }) => (
    <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative shrink-0 w-12 h-7 rounded-full transition-colors ${checked ? "bg-yellow" : "bg-zinc-700"}`}
    >
        <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white transition-transform ${checked ? "translate-x-5" : ""}`} />
    </button>
);

const LobbySettingsModal = ({ room, teams, settings, onClose }) => {
    const [draft, setDraft] = useState(settings);
    const [isSaving, setIsSaving] = useState(false);

    const library = getRoomLibrary(room);
    const categoryAttributes = room.library ? getCategoryAttributes(library) : [];
    const sortedTeams = sortTeams(teams);

    const update = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));
    const updateRule = (attribute, key, value) =>
        setDraft((prev) => ({
            ...prev,
            attributeRules: { ...prev.attributeRules, [attribute]: { ...prev.attributeRules[attribute], [key]: value } },
        }));

    const handleSave = async () => {
        // Blank fields: no limit for max per value, 0 for the rest
        const attributeRules = Object.fromEntries(
            Object.entries(draft.attributeRules).map(([attribute, rule]) => [attribute, {
                minDifferent    :   rule.minDifferent === "" || rule.minDifferent === undefined ? 0 : rule.minDifferent,
                maxPerValue     :   rule.maxPerValue === "" || rule.maxPerValue === null ? null : rule.maxPerValue,
                bonusOneValue   :   rule.bonusOneValue === "" ? 0 : rule.bonusOneValue,
                bonusEveryValue :   rule.bonusEveryValue === "" ? 0 : rule.bonusEveryValue,
            }])
        );
        const cleaned = { ...draft, attributeRules };

        const problem = getSettingsProblem(cleaned, sortedTeams, categoryAttributes);
        if (problem) {
            toast.error(problem);
            return;
        }

        setIsSaving(true);
        const saved = await saveAuctionSettings(room.id, cleaned, sortedTeams);
        setIsSaving(false);
        if (saved) {
            toast.success("Settings saved");
            onClose();
        } else {
            toast.error("Couldn't save settings");
        }
    };

    const inputClasses = "w-full rounded-xl border border-zinc-700 bg-black p-3 text-lg text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-yellow";
    const sectionTitle = "mt-8 mb-3 text-sm uppercase tracking-widest text-zinc-500";

    const NumberField = ({ field }) => (
        <label key={field.key} className={field.wide ? "sm:col-span-2" : ""}>
            <span className="flex items-center mb-1 text-zinc-300">
                {field.label}
                {field.tip && <InfoTip label={field.label}><TipText lines={field.tip(draft)} /></InfoTip>}
            </span>
            <input
                type="number"
                min={field.min}
                max={field.max}
                value={draft[field.key]}
                onChange={(e) => update(field.key, toNumberOrBlank(e.target.value))}
                className={inputClasses}
            />
        </label>
    );

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onClick={onClose}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
        >
            <motion.div
                initial={{ scale: 0.95, y: 10 }}
                animate={{ scale: 1, y: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl border border-zinc-800 bg-zinc-950 p-6 md:p-8 text-white text-left"
            >
                <button onClick={onClose} aria-label="Close" className="absolute top-5 right-5 text-zinc-500 hover:text-white">
                    <X size={24} />
                </button>

                <h2 className="text-3xl font-extrabold">Lobby settings</h2>

                {/* --- Teams --- */}
                <h3 className={sectionTitle}>Teams</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {teamFields.map((field) => NumberField({ field }))}
                </div>

                {/* --- Constraints --- */}
                <h3 className={sectionTitle}>Constraints</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {constraintFields.map((field) => NumberField({ field }))}
                </div>

                {/* --- Bidding --- */}
                <h3 className={sectionTitle}>Bidding</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {bidFields.map((field) => NumberField({ field }))}
                </div>

                {/* --- Per attribute --- */}
                {categoryAttributes.map((attribute) => (
                    <div key={attribute.name}>
                        <h3 className={sectionTitle}>
                            {attribute.name} <span className="normal-case tracking-normal">· {attribute.values.length} values</span>
                        </h3>
                        <div className="grid grid-cols-2 gap-4">
                            {ruleFields.map(({ key, label, placeholder }) => (
                                <label key={key}>
                                    <span className="flex items-center mb-1 text-zinc-300">
                                        {label}
                                        <InfoTip label={`${attribute.name}: ${label}`}><TipText lines={ruleTip(key, attribute, draft)} /></InfoTip>
                                    </span>
                                    <input
                                        type="number"
                                        min={0}
                                        placeholder={placeholder}
                                        value={draft.attributeRules[attribute.name]?.[key] ?? ""}
                                        onChange={(e) => updateRule(attribute.name, key, toNumberOrBlank(e.target.value))}
                                        className={inputClasses}
                                    />
                                    {key === "bonusEveryValue" && (
                                        <span className="block mt-1 text-sm text-zinc-500">
                                            {allDifferentHint(Number(draft.itemsToSubmit) || 0, attribute.values.length)}
                                        </span>
                                    )}
                                </label>
                            ))}
                        </div>
                    </div>
                ))}
                {!room.library && (
                    <p className="mt-4 text-sm text-zinc-500">Choose a library to set limits and bonuses for its attributes.</p>
                )}

                {/* --- Flow --- */}
                <h3 className={sectionTitle}>Flow</h3>
                <label>
                    <span className="flex items-center mb-1 text-zinc-300">
                        Take a break after every … items sold
                        <InfoTip label="Breaks">
                            <TipText lines={["Everyone gets a pause screen with budgets and items so far. You resume when ready.",
                                             "e.g. 10: a break after the 10th, 20th and 30th sale. Skipped items don't count."]} />
                        </InfoTip>
                    </span>
                    <input
                        type="number"
                        min={0}
                        value={draft.breakAfter}
                        onChange={(e) => update("breakAfter", toNumberOrBlank(e.target.value))}
                        className={inputClasses}
                    />
                    <span className="block mt-1 text-sm text-zinc-500">0 means no breaks. Otherwise the next item comes up straight after a sale.</span>
                </label>
                <div className="mt-5 flex items-center justify-between gap-4">
                    <div>
                        <div className="text-lg">Bring back unsold items</div>
                        <div className="text-sm text-zinc-500">Items the host skips come up once more, after the last item.</div>
                    </div>
                    <Toggle checked={draft.bringBackUnsold} onChange={(value) => update("bringBackUnsold", value)} />
                </div>

                {/* --- Visibility --- */}
                <h3 className={sectionTitle}>What everyone can see</h3>
                <div className="space-y-4">
                    {visibilityFields.map(({ key, label, hint }) => (
                        <div key={key} className="flex items-center justify-between gap-4">
                            <div>
                                <div className="text-lg">{label}</div>
                                <div className="text-sm text-zinc-500">{hint}</div>
                            </div>
                            <Toggle checked={draft[key]} onChange={(value) => update(key, value)} />
                        </div>
                    ))}
                </div>

                <Button
                    variant="main"
                    size="lg"
                    onClick={handleSave}
                    disabled={isSaving}
                    className="mt-10 w-full rounded-xl"
                >
                    {isSaving ? "Saving..." : "Save settings"}
                </Button>
            </motion.div>
        </motion.div>
    );
};

export default LobbySettingsModal;
